/**
 * Phase 5 — single source of truth for marketplace narrative copy.
 * Do not hardcode informational text in layout components; import from here.
 */

export type MarketplaceFaq = {
  id: string;
  question: string;
  answer: string;
};

export type KnowledgeTile = {
  id: string;
  title: string;
  summary: string;
  tag: string;
};

export type GenesisLogisticsRoute = {
  id: string;
  origin: string;
  destination: string;
  amountUsdc: string;
  status: 'PENDING_FUNDING' | 'FUNDED' | 'CLAIMED' | 'DISPUTED' | 'SETTLED';
  gigType: string;
};

export type MegaMenuColumn = {
  id: string;
  title: string;
  subtitle: string;
  icon: 'merchant' | 'runner' | 'arbitration';
  href: string;
  bullets: string[];
};

export const marketplaceBrand = {
  name: 'ShopperZulu',
  tagline: 'The Better Xchange — peer-to-peer trading made simple',
  heroEyebrow: 'ShopperZulu',
  heroTitle: 'Find it. Talk. Settle your way.',
  heroSubtitle:
    'Listings, chat, and community rails — zero custody on the communications layer.',
};

export const marketplaceView = {
  title: 'Jamaica Marketplace',
  subtitle: 'Categories first — Craigslist-style browse with area zoom across 25 Jamaican cities.',
};

export const runnerHubView = {
  title: 'Runner Cockpit',
  subtitle: 'Claim funded gigs, track live lanes, and complete Handshake PIN settlement in the field.',
};

export const trustEngineSection = {
  id: 'trust-engine',
  title: 'The Trust Engine',
  subtitle: 'Web3 conditional escrow removes Web2 chargeback vulnerability',
  steps: [
    {
      id: 'lock',
      label: 'Lock',
      body: 'Merchant signs an on-chain escrow deposit. Funds sit in a non-custodial vault — not on our balance sheet.',
    },
    {
      id: 'execute',
      label: 'Execute',
      body: 'Runner claims the gig, completes field handoff, and enters the 6-digit Handshake PIN at pickup.',
    },
    {
      id: 'release',
      label: 'Release',
      body: 'Oracle settlement releases USDC to the runner wallet. Disputes route to the Arbitration Engine.',
    },
  ],
};

export const capitalRoutingSection = {
  id: 'capital-routing',
  title: 'Capital Routing',
  subtitle: 'From liquidity spoke to localized runner payout in minutes',
  steps: [
    {
      id: 'spoke',
      label: 'Spoke funding',
      body: 'Merchant wallet funds escrow on the active chain spoke (Anvil Hub / Spoke topology in dev).',
    },
    {
      id: 'hub',
      label: 'Hub clearing',
      body: 'Cross-chain relayer confirms Hyperlane clearing events before the gig goes live on the noticeboard.',
    },
    {
      id: 'payout',
      label: 'Runner payout',
      body: 'Verified handoff triggers instant localized payout — no platform float, no chargeback window.',
    },
  ],
};

export const knowledgeHubSection = {
  id: 'knowledge-hub',
  title: 'The Knowledge Hub',
  subtitle: 'Technical parameters and field execution guarantees',
  tiles: [
    {
      id: 'handshake-pin',
      title: '6-Digit Handshake Oracle',
      summary: 'Pickup verification PIN gates on-chain release. Wrong PIN blocks settlement until dispute.',
      tag: 'Field ops',
    },
    {
      id: 'hyperlane',
      title: 'Hyperlane Clearing Index',
      summary: 'Omnichain fund events indexed for Hub confirmation before noticeboard visibility.',
      tag: 'Omnichain',
    },
    {
      id: 'arbitration',
      title: 'Single-Secret Arbitration',
      summary: 'Governance telemetry with merchant/runner ruling paths and on-chain refund execution.',
      tag: 'Governance',
    },
    {
      id: 'non-custodial',
      title: 'Zero Custody Architecture',
      summary: 'Private keys never touch the server. All financial flows route through client-side Web3 widgets.',
      tag: 'Security',
    },
  ] as KnowledgeTile[],
};

export const megaMenuColumns: MegaMenuColumn[] = [
  {
    id: 'merchant-suite',
    title: 'The Merchant Suite',
    subtitle: 'Escrow locking & multi-chain routing',
    icon: 'merchant',
    href: '/merchant',
    bullets: [
      'Workspace hub for all your stores',
      'Logistics dispatch or rental operator templates',
      'Isolated ledger per /store/[slug]',
    ],
  },
  {
    id: 'runner-cockpit',
    title: 'The Runner Cockpit',
    subtitle: 'Field tracking & Handshake oracle release',
    icon: 'runner',
    href: '/runner',
    bullets: [
      'Claim funded logistics gigs',
      'Confirm handoff at pickup',
      'Enter 6-digit PIN for settlement',
    ],
  },
  {
    id: 'arbitration-engine',
    title: 'The Arbitration Engine',
    subtitle: 'Governance telemetry & secret overrides',
    icon: 'arbitration',
    href: '/admin',
    bullets: [
      'Review disputed escrow lanes',
      'Rule merchant or runner favor',
      'Execute on-chain refund / release',
    ],
  },
];

export const connectivityLabels = {
  api: 'Noticeboard API',
  hyperlane: 'Hyperlane Index',
  anvil: 'Anvil Escrow',
  postgres: 'Postgres Ledger',
};

/** Fallback ticker lanes when feed is loading — mirrors Genesis seed dictionary. */
export const genesisLogisticsRoutes: GenesisLogisticsRoute[] = [
  {
    id: 'genesis-portland-kingston',
    origin: 'Portland Depot',
    destination: 'Kingston Export Terminal',
    amountUsdc: '500.00',
    status: 'FUNDED',
    gigType: 'logistics',
  },
  {
    id: 'genesis-kingston-loop',
    origin: 'Kingston Hub',
    destination: 'Montego Bay Handoff',
    amountUsdc: '320.00',
    status: 'CLAIMED',
    gigType: 'logistics',
  },
  {
    id: 'genesis-agro-mulch',
    origin: 'Blue Mountains Nursery',
    destination: 'Kingston Agroforestry Yard',
    amountUsdc: '180.00',
    status: 'FUNDED',
    gigType: 'agroforestry',
  },
  {
    id: 'genesis-fabrication',
    origin: 'Spanish Town Fabrication',
    destination: 'Portmore Assembly Dock',
    amountUsdc: '240.00',
    status: 'PENDING_FUNDING',
    gigType: 'fabrication',
  },
  {
    id: 'genesis-settled',
    origin: 'Portland Depot',
    destination: 'Kingston Export Terminal',
    amountUsdc: '500.00',
    status: 'SETTLED',
    gigType: 'logistics',
  },
];

export const noticeboardSection = {
  title: 'Live Noticeboard',
  subtitle: 'Funded gigs from the Genesis network — claim to open your Runner Cockpit',
  liveBadge: 'LIVE',
  emptyTitle: 'The noticeboard is waiting for new listings',
  emptyBody: 'Merchants publish funded gigs here once escrow is confirmed on-chain.',
  walletInterceptTitle: 'Link your Web3 Wallet to Start Transacting',
  walletInterceptBody:
    'Claiming a gig requires a connected wallet for on-chain payout. Connect MetaMask or Rabby and sign the linking message to continue.',
};

export const rentalSafetyFaqs: MarketplaceFaq[] = [
  {
    id: 'rental-chargeback',
    question: 'How does escrow protect rental merchants from chargeback fraud?',
    answer:
      'Guest funds lock on the Spoke before check-in. Release requires the guest 6-digit Handshake PIN at physical handoff — eliminating card-network reversals and no-show chargebacks.',
  },
  {
    id: 'rental-payout',
    question: 'When do I receive payout on a rental booking?',
    answer:
      'Instantly on verified check-in. When you enter the guest PIN at handoff, the oracle broadcasts settlement and USDC releases to your merchant wallet on-chain.',
  },
  {
    id: 'rental-dispute',
    question: 'What if a guest disputes check-in?',
    answer:
      'Settlement pauses until the Arbitration Engine reviews ledger evidence. Escrow remains non-custodial — no platform float.',
  },
];

export const faqs: MarketplaceFaq[] = [
  {
    id: 'chargeback',
    question: 'How does BetterXchange eliminate chargebacks?',
    answer:
      'Escrow is locked on-chain before field work begins. Release requires Handshake PIN verification — there is no card network reversal window.',
  },
  {
    id: 'custody',
    question: 'Does BetterXchange hold my funds?',
    answer:
      'No. The platform never custody assets or private keys. Merchants and runners interact directly with public smart contracts.',
  },
  ...rentalSafetyFaqs,
];

/** Phase 6 — rental, fleet, and equipment asset schemas for PMS + storefronts. */
export type RentalAssetSchema = {
  id: string;
  label: string;
  category: 'rental_property' | 'vehicle_fleet' | 'heavy_equipment';
  unitLabel: string;
  fields: Array<{ id: string; label: string; placeholder?: string }>;
  escrowHint: string;
};

export const rentalAssetClasses = {
  title: 'Sovereign Rental & Fleet Inventory',
  subtitle: 'Property, vehicles, and equipment with on-chain escrow guarantees',
  types: [
    {
      id: 'rental_property',
      label: 'Rental property',
      category: 'rental_property' as const,
      unitLabel: 'night',
      fields: [
        { id: 'address', label: 'Property address', placeholder: '12 Hope Rd, Kingston' },
        { id: 'bedrooms', label: 'Bedrooms', placeholder: '2' },
        { id: 'check_in', label: 'Check-in time', placeholder: '3:00 PM' },
      ],
      escrowHint: 'Deposit locked on Spoke until guest check-in PIN verifies.',
    },
    {
      id: 'vehicle_fleet',
      label: 'Vehicle fleet',
      category: 'vehicle_fleet' as const,
      unitLabel: 'day',
      fields: [
        { id: 'make_model', label: 'Make & model', placeholder: 'Toyota Corolla 2022' },
        { id: 'plate', label: 'Plate / fleet ID', placeholder: 'JMA-4821' },
        { id: 'mileage_cap', label: 'Daily mileage cap', placeholder: '200 km' },
      ],
      escrowHint: 'Rental fee escrowed until handoff PIN at vehicle pickup.',
    },
    {
      id: 'heavy_equipment',
      label: 'Heavy equipment',
      category: 'heavy_equipment' as const,
      unitLabel: 'shift',
      fields: [
        { id: 'equipment_type', label: 'Equipment type', placeholder: 'Mini excavator' },
        { id: 'serial', label: 'Serial / asset tag', placeholder: 'CAT-302-8812' },
        { id: 'operator_required', label: 'Operator included', placeholder: 'Yes / No' },
      ],
      escrowHint: 'Shift fee held in escrow until on-site check-in PIN release.',
    },
  ] as RentalAssetSchema[],
};

export const flippedHandshakeCopy = {
  eyebrow: 'Flipped Handshake — rental check-in',
  title: 'Merchant check-in',
  subtitle:
    'You provide the asset. The customer holds the 6-digit PIN. Enter their PIN at physical check-in to release escrow instantly.',
  steps: [
    'Confirm the customer has arrived at check-in or vehicle handoff.',
    'Ask the customer for their 6-digit Handshake PIN (sent when booking escrow locked).',
    'Enter the PIN below — oracle settlement releases Spoke stablecoins to your wallet.',
  ],
  pinHint: 'Enter the customer check-in PIN to release escrow payout.',
  checkInButton: 'Customer arrived — enter check-in PIN',
};

export const storefrontCopy = {
  ecosystemHatch:
    'Powered by BetterXchange — Explore the broader decentralized economy',
  ecosystemLinkLabel: 'Open marketplace',
  bookCta: 'Book with escrow',
  bookAuthTitle: 'Sign in to book',
  bookAuthBody:
    'Connect your wallet or create an account to reserve this listing with non-custodial escrow protection.',
  emptyInventory: 'This merchant has no live listings yet.',
  viewStorefront: 'View public storefront',
};

export const merchantPmsCopy = {
  title: 'Store back-office',
  subtitle: 'Order book, messaging, accounting export, and storefront branding for this workspace',
  orderBookTitle: 'Visual order book',
  orderBookHint: 'Active, claimed, and pending rental slots by deadline.',
  logisticsBoardTitle: 'Dispatch order book',
  logisticsBoardHint: 'List view for funded logistics lanes tied to this store.',
  inboxTitle: 'Booking messages',
  inboxHint: 'Sharetribe inbox for active booking conversations.',
  exportTitle: 'Accounting export',
  exportHint: 'Download CSV scoped to this store_id only.',
  exportButton: 'Export ledger to CSV',
  customizerTitle: 'Storefront customizer',
  customizerHint: 'Banner, logo, and bio shown on your public /store/[slug] page.',
  saveStorefront: 'Save storefront branding',
  openCheckIn: 'Open merchant check-in',
};

export const merchantWorkspaceCopy = {
  hubTitle: 'Merchant Workspace Hub',
  hubSubtitle: 'Choose which business you are managing today. Each store has its own ledger and public storefront.',
  personaEyebrow: 'Merchant persona',
  personaTitle: 'Back-office workspace',
  personaBody: 'Escrow, listings, and accounting for this store only — separate from the Runner field lane.',
  runnerPersonaEyebrow: 'Runner persona',
  runnerPersonaTitle: 'Field operations lane',
  runnerPersonaBody: 'Claim gigs, confirm handoffs, and enter Handshake PINs — separate from Merchant back-office.',
  switchToRunner: 'Switch to Runner lane →',
  switchToMerchant: 'Switch to Merchant hub →',
  createStoreTitle: 'Create new store',
  createStoreCta: 'Create new store',
  stepTemplate: 'Choose operational model',
  stepSlug: 'Claim your public URL slug',
  stepBranding: 'Brand your storefront',
  logisticsTemplateTitle: 'Logistics Dispatch',
  logisticsTemplateBody: 'List-based order book for noticeboard gigs, runners, and escrow lanes.',
  rentalTemplateTitle: 'Rental Operator',
  rentalTemplateBody: 'Calendar order book for properties, fleet, and equipment rentals.',
  manageStore: 'Manage store',
  publicStore: 'Public storefront',
  noStoresTitle: 'No stores yet',
  noStoresBody: 'Create your first storefront workspace to publish listings under a dedicated /store/[slug] path.',
  rentalRunnerNotice:
    'This is a rental listing — check-in and PIN entry happen in the Merchant back-office, not the Runner lane.',
};

export const personaCopy = {
  signupPersonaLegend: 'How will you primarily use BetterXchange?',
  signupStandardTitle: 'Standard User',
  signupStandardBody: 'Browse marketplace listings, book services, and manage personal activity.',
  signupMerchantTitle: 'Merchant / Business',
  signupMerchantBody: 'Open store workspaces, publish listings, and export per-store accounting.',
  signupRunnerTitle: 'Runner / Fleet Ops',
  signupRunnerBody: 'Claim logistics gigs, confirm handoffs, and complete Handshake PIN settlement.',
  personaLabels: {
    standard: 'Standard',
    merchant: 'Merchant',
    runner: 'Runner',
  },
  activePersonaLabel: 'Active persona',
  switchToPersona: 'Switch to [persona]',
  managePersonasLink: 'Profile & personas',
  switchModalTitle: 'Persona switch initiated',
  switchModalBody:
    'Please connect your designated [persona] wallet to continue. MetaMask or Rabby will prompt you to select the correct account.',
  switchModalConnect: 'Connect designated wallet',
  merchantWalletLabel: 'Merchant',
  runnerWalletLabel: 'Runner',
  restrictedMerchantTitle: 'Merchant workspace restricted',
  restrictedRunnerTitle: 'Runner lane restricted',
  restrictedSwitchBody:
    'This route belongs to a different operational persona. Switch personas to continue.',
  restrictedActivateBody:
    'Activate this persona in your profile hub to access this lane.',
  switchPersonaCta: 'Switch to [persona] persona',
  openProfileHub: 'Open profile hub',
  profileHubTitle: 'Profile & personas',
  profileHubSubtitle:
    'Manage account details and bind separate Web3 wallets to Merchant and Runner personas.',
  profileTabGlobal: 'Global',
  profileTabMerchant: 'Merchant',
  profileTabRunner: 'Runner',
  profileGlobalTitle: 'Account details',
  profileGlobalBody: 'Update email, password, and standard profile settings.',
  profileContactLink: 'Contact details & email',
  profilePasswordLink: 'Change password',
  profileAccountLink: 'Manage account',
  profileMerchantTitle: 'Merchant wallet delegation',
  profileMerchantBody:
    'Bind the wallet that signs merchant escrow and store operations. Keeps funds air-gapped from your Runner wallet.',
  profileRunnerTitle: 'Runner wallet delegation',
  profileRunnerBody:
    'Bind the wallet that receives field payouts. Separate from your Merchant treasury wallet.',
  merchantWalletField: 'Designated Merchant wallet',
  runnerWalletField: 'Designated Runner wallet',
  useConnectedWallet: 'Use currently connected wallet',
  saveDelegation: 'Save wallet delegation',
  profileSaved: 'Profile updated.',
  personaActivated: '[persona] persona activated.',
  activateMerchant: 'Activate Merchant persona',
  activateRunner: 'Activate Runner persona',
  currentSessionWallet: 'Connected session wallet',
  profileAuthRequired: 'Sign in with your account or connect a wallet to manage personas.',
  profileLoginLink: 'Go to login',
};

export default {
  marketplaceBrand,
  trustEngineSection,
  capitalRoutingSection,
  knowledgeHubSection,
  megaMenuColumns,
  connectivityLabels,
  genesisLogisticsRoutes,
  noticeboardSection,
  faqs,
  rentalAssetClasses,
  rentalSafetyFaqs,
  flippedHandshakeCopy,
  storefrontCopy,
  merchantPmsCopy,
  merchantWorkspaceCopy,
  personaCopy,
};
